import { useEffect } from "react";

type PageStatus = "실연동" | "부분" | "목업" | "계산도구" | "준비중";

const StatusBadge = ({ status }: { status: PageStatus }) => {
  const style: Record<PageStatus, string> = {
    실연동: "bg-emerald-50 text-emerald-700 border-emerald-200",
    부분: "bg-sky-50 text-sky-700 border-sky-200",
    목업: "bg-amber-50 text-amber-700 border-amber-200",
    계산도구: "bg-[#C4956A]/10 text-[#C4956A] border-[#C4956A]/30",
    준비중: "bg-[#F0EDE8] text-[#8C8780] border-[#E8E5E0]",
  };
  return (
    <span className={`inline-block px-1.5 py-0.5 text-[10px] font-semibold rounded border whitespace-nowrap ${style[status]}`}>
      {status}
    </span>
  );
};

export default function ProductPRDPage() {
  useEffect(() => {
    document.title = "강스터디 - 제품 PRD";
  }, []);

  return (
    <>
      <style>{`
        @media print {
          @page { size: A4; margin: 18mm 15mm 20mm 15mm; }
          body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        }
      `}</style>

      <div className="min-h-screen bg-white font-['Noto_Sans_KR'] text-[#1C1917] print:bg-white">
        <div className="max-w-[210mm] mx-auto px-8 py-10 print:px-0 print:py-0">

          <div className="mb-8 print:hidden">
            <button
              onClick={() => window.print()}
              className="inline-flex items-center gap-2 px-5 py-3 bg-[#1C1917] text-white text-sm font-semibold rounded-lg hover:bg-[#3A2E28] transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-printer-line"></i>
              PDF로 저장하기 (Ctrl+P)
            </button>
            <p className="text-xs text-[#8C8780] mt-2">인쇄 대화상자에서 "PDF로 저장"을 선택하세요</p>
          </div>

          {/* ===== COVER ===== */}
          <div className="mb-10 pb-8 border-b-2 border-[#1C1917]">
            <div className="flex items-center justify-between mb-6">
              <div>
                <p className="text-xs font-semibold text-[#C4956A] uppercase tracking-widest mb-2">
                  Product Requirements Document
                </p>
                <h1 className="text-3xl font-bold text-[#1C1917] leading-tight">강스터디</h1>
                <p className="text-lg text-[#5C5650] mt-1">Kangstudy — 실전 경매·부동산 투자 교육 플랫폼</p>
              </div>
              <div className="text-right text-xs text-[#8C8780] space-y-0.5">
                <p>버전 1.0</p>
                <p>작성일 2026-08-18</p>
                <p>study.echotwin.kr</p>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-0 text-sm border-t border-[#E8E5E0] pt-6">
              <div className="flex gap-4 py-1">
                <span className="text-[#8C8780] w-20 flex-shrink-0">제품명</span>
                <span className="font-semibold">강스터디 (Kangstudy)</span>
              </div>
              <div className="flex gap-4 py-1">
                <span className="text-[#8C8780] w-20 flex-shrink-0">구분</span>
                <span className="font-semibold">웹 애플리케이션 (SPA, React 19)</span>
              </div>
              <div className="flex gap-4 py-1">
                <span className="text-[#8C8780] w-20 flex-shrink-0">대상</span>
                <span className="font-semibold">초보 투자자·매매사업자·은퇴자·직장인·여성 투자자</span>
              </div>
              <div className="flex gap-4 py-1">
                <span className="text-[#8C8780] w-20 flex-shrink-0">상태</span>
                <span className="font-semibold text-emerald-600">라이브 서비스 운영 중</span>
              </div>
            </div>
          </div>

          {/* ===== 1. 제품 개요 ===== */}
          <section className="mb-10">
            <h2 className="text-lg font-bold text-[#1C1917] border-b border-[#E8E5E0] pb-2 mb-4">1. 제품 개요</h2>
            <p className="text-sm text-[#5C5650] leading-relaxed">
              강스터디는 <strong className="text-[#1C1917]">초보자도 따라올 수 있는 실전 경매·부동산 투자 교육 플랫폼</strong>입니다.
              전문가가 큐레이션한 경매 물건(전문가 PICK), 체계적인 교육과정·동영상 강의, 1:1 상담, 물건분석 리포트,
              AI 기반 투자 판단 도구(입찰가 계산기·투자 시뮬레이터·물건 비교), 커뮤니티를 제공합니다.
              백엔드는 자매 서비스 <strong className="text-[#1C1917]">EchoTwin</strong>과 Supabase 프로젝트·인증(auth.users)을 공유하며,
              매물 원본 데이터는 EchoTwin의 API worker를 통해 가공·인증된 형태로만 공급받습니다.
            </p>
          </section>

          {/* ===== 2. 정보구조 / 페이지 목록 ===== */}
          <section className="mb-10">
            <h2 className="text-lg font-bold text-[#1C1917] border-b border-[#E8E5E0] pb-2 mb-4">2. 정보구조 — 페이지 목록 및 데이터 상태</h2>
            <p className="text-xs text-[#8C8780] mb-3">
              <StatusBadge status="실연동" /> Supabase/EchoTwin API 실데이터 &nbsp;
              <StatusBadge status="목업" /> src/mocks/ 정적 데이터 구동 &nbsp;
              <StatusBadge status="계산도구" /> 클라이언트 연산, DB 불필요 &nbsp;
              <StatusBadge status="준비중" /> ComingSoon 플레이스홀더
            </p>
            <div className="overflow-x-auto">
              <table className="w-full text-xs border-collapse">
                <thead>
                  <tr className="bg-[#FAFAF8] border-b border-[#E8E5E0]">
                    <th className="text-left py-2 px-3 font-semibold text-[#1C1917] w-36">페이지</th>
                    <th className="text-left py-2 px-3 font-semibold text-[#1C1917] w-40">라우트</th>
                    <th className="text-left py-2 px-3 font-semibold text-[#1C1917] w-20">상태</th>
                    <th className="text-left py-2 px-3 font-semibold text-[#1C1917]">설명</th>
                  </tr>
                </thead>
                <tbody className="text-[#5C5650]">
                  {[
                    ["메인", "/", "실연동", "히어로, 전문가 PICK, 교육과정, 블로그, 후기 섹션"],
                    ["전문가 PICK 목록/상세", "/auctions, /auctions/:id", "실연동", "EchoTwin auction_cases에서 매일 06:30 큐레이션한 60건"],
                    ["교육과정 목록/상세", "/courses, /courses/:id", "실연동", "kangstudy_courses (2026-08-18 실연동)"],
                    ["동영상 강의", "/lectures, /lectures/:id", "목업", "src/mocks/lectures.ts"],
                    ["자료실", "/materials", "실연동", "kangstudy_materials, 서명 업로드 URL 방식"],
                    ["상담신청", "/consultation", "실연동", "kangstudy-submit Edge Function 경유 (제출 전용)"],
                    ["리포트 구매", "/reports", "목업", "src/mocks/reports.ts"],
                    ["입찰가 계산기", "/bid-calculator, /bid-calculator-guide", "계산도구", "일반방식+시세역산방식, 물건 상세에서 pre-fill"],
                    ["물건 비교 계산기", "/compare", "목업+계산도구", "비교 대상은 목업, 연산은 클라이언트"],
                    ["AI 투자 시뮬레이터", "/investment-simulator", "계산도구", "5단계 연산 엔진 + AI 100점 점수 엔진"],
                    ["낙찰물건 인사이트", "/awarded-insights, /:id", "목업", "src/mocks/awardedItems.ts, 사후 분석 6건"],
                    ["로그인/회원가입", "/login, /register", "실연동", "Supabase Auth (EchoTwin과 계정 공유)"],
                    ["마이페이지", "/mypage/:section", "목업", "src/mocks/mypage.ts, RequireAuth로 보호"],
                    ["커뮤니티", "/community/:section", "실연동", "kangstudy_posts/comments/likes (2026-08-18 실연동)"],
                    ["블로그", "/blog, /blog/:slug", "실연동", "kangstudy_blog_posts, 매일 07:00 자동 생성"],
                    ["관리자 대시보드", "/admin/:section", "부분", "폼 접수함 실연동, 나머지 회원/결제/통계는 목업(admin.ts)"],
                    ["AI 경매비서 (챗봇)", "전 페이지 위젯", "실연동", "/api/chat (Gemini 프록시), 추천 물건 3건은 하드코딩 잔존"],
                    ["소개/후기/FAQ/문의/오시는길/약관", "/about, /reviews, /faq, /contact, /location, /terms", "준비중", "ComingSoon 플레이스홀더"],
                  ].map((row, i) => (
                    <tr key={i} className="border-b border-[#F0EDE8]">
                      <td className="py-2 px-3 font-medium text-[#1C1917]">{row[0]}</td>
                      <td className="py-2 px-3 font-mono text-[10px]">{row[1]}</td>
                      <td className="py-2 px-3">
                        {row[2].split("+").map((s, j) => (
                          <span key={j} className="mr-1"><StatusBadge status={s as PageStatus} /></span>
                        ))}
                      </td>
                      <td className="py-2 px-3">{row[3]}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          {/* ===== 3. 시스템 아키텍처 ===== */}
          <section className="mb-10">
            <h2 className="text-lg font-bold text-[#1C1917] border-b border-[#E8E5E0] pb-2 mb-4">3. 시스템 아키텍처 — 백엔드 접점</h2>
            <p className="text-sm text-[#5C5650] mb-4 leading-relaxed">
              프론트는 자체 서버 코드를 갖지 않는다. 오너(EchoTwin) 인프라와의 접점은 다섯 곳으로 한정된다.
            </p>
            <div className="overflow-x-auto">
              <table className="w-full text-xs border-collapse">
                <thead>
                  <tr className="bg-[#FAFAF8] border-b border-[#E8E5E0]">
                    <th className="text-left py-2 px-3 font-semibold text-[#1C1917] w-40">접점</th>
                    <th className="text-left py-2 px-3 font-semibold text-[#1C1917]">역할</th>
                    <th className="text-left py-2 px-3 font-semibold text-[#1C1917] w-48">코드 위치</th>
                  </tr>
                </thead>
                <tbody className="text-[#5C5650]">
                  <tr className="border-b border-[#F0EDE8]">
                    <td className="py-2 px-3 font-medium text-[#1C1917]">Supabase 읽기</td>
                    <td className="py-2 px-3">전문가 PICK·교육과정·커뮤니티·블로그·자료실·인증. 읽기는 공개, 쓰기는 전부 RLS로 차단</td>
                    <td className="py-2 px-3 font-mono text-[10px]">src/lib/supabase.ts, src/hooks/*</td>
                  </tr>
                  <tr className="border-b border-[#F0EDE8]">
                    <td className="py-2 px-3 font-medium text-[#1C1917]">EchoTwin API worker</td>
                    <td className="py-2 px-3">매물 상세·분석·시세 전체 데이터. service_role로 가공·인증·필드 통제 후 공급 (원본 직접 조회 금지)</td>
                    <td className="py-2 px-3 font-mono text-[10px]">src/lib/echotwin-api.ts</td>
                  </tr>
                  <tr className="border-b border-[#F0EDE8]">
                    <td className="py-2 px-3 font-medium text-[#1C1917]">kangstudy-submit</td>
                    <td className="py-2 px-3">공개 폼 5종 제출 대행(상담·교육신청·리포트·뉴스레터·강의문의), IP 시간당 10건 제한</td>
                    <td className="py-2 px-3 font-mono text-[10px]">src/lib/submit-form.ts</td>
                  </tr>
                  <tr className="border-b border-[#F0EDE8]">
                    <td className="py-2 px-3 font-medium text-[#1C1917]">kangstudy-admin</td>
                    <td className="py-2 px-3">관리자 쓰기 대행 + 폼 접수함 조회, JWT 검증 후 ADMIN_USER_IDS 대조</td>
                    <td className="py-2 px-3 font-mono text-[10px]">src/lib/admin-api.ts</td>
                  </tr>
                  <tr className="border-b border-[#F0EDE8]">
                    <td className="py-2 px-3 font-medium text-[#1C1917]">/api/chat</td>
                    <td className="py-2 px-3">Gemini 챗봇 프록시(Pages Function), 키 서버 보관 + IP·일일 호출 상한(200/일)</td>
                    <td className="py-2 px-3 font-mono text-[10px]">functions/api/chat.ts</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-xs text-[#8C8780] mt-3">이미지는 전부 자체 CDN <span className="font-mono">img.echotwin.kr/kangstudy/</span> (Cloudflare R2)</p>
          </section>

          {/* ===== 4. 데이터 경계 규칙 ===== */}
          <section className="mb-10">
            <h2 className="text-lg font-bold text-[#1C1917] border-b border-[#E8E5E0] pb-2 mb-4">4. 데이터 경계 규칙 (보안)</h2>
            <p className="text-sm text-[#5C5650] mb-3 leading-relaxed">
              이 앱의 DB는 오너의 다른 서비스(EchoTwin)와 <strong className="text-[#1C1917]">한 Supabase 프로젝트를 공유</strong>한다.
              아래를 어기면 공유 서비스가 깨진다.
            </p>
            <div className="space-y-2 text-xs text-[#5C5650]">
              <div className="flex items-start gap-2">
                <i className="ri-shield-cross-line text-rose-500 mt-0.5"></i>
                <span><strong className="text-[#1C1917]">프론트에서 insert/update/delete 직접 호출 금지.</strong> 강스터디 테이블은 전부 RLS로 쓰기가 잠겨 있어 에러 없이 조용히 0행이 된다(PostgREST가 204를 준다). 쓰기는 반드시 admin-api.ts / submit-form.ts 헬퍼 경유.</span>
              </div>
              <div className="flex items-start gap-2">
                <i className="ri-shield-cross-line text-rose-500 mt-0.5"></i>
                <span><strong className="text-[#1C1917]">DB 스키마·RLS·Edge Function 변경은 오너에게 요청.</strong> 이 저장소에는 그 코드가 없다(의도된 것). 새 테이블은 kangstudy_ 접두사로 요청.</span>
              </div>
              <div className="flex items-start gap-2">
                <i className="ri-shield-cross-line text-rose-500 mt-0.5"></i>
                <span><strong className="text-[#1C1917]">API 키·시크릿을 클라이언트 코드에 넣지 말 것.</strong> VITE_* 는 번들에 평문으로 박힌다. 공개해도 되는 값(anon key)만 허용, 서버 키는 Pages Function으로.</span>
              </div>
              <div className="flex items-start gap-2">
                <i className="ri-shield-cross-line text-rose-500 mt-0.5"></i>
                <span><strong className="text-[#1C1917]">관리자 판정을 클라이언트에서 하지 말 것.</strong> 정본은 서버 env 하나, UI 게이팅은 useAuth().isAdmin(서버 조회 결과)만 사용.</span>
              </div>
            </div>
          </section>

          {/* ===== 5. 기술 스택 ===== */}
          <section className="mb-10">
            <h2 className="text-lg font-bold text-[#1C1917] border-b border-[#E8E5E0] pb-2 mb-4">5. 기술 스택</h2>
            <div className="overflow-x-auto">
              <table className="w-full text-xs border-collapse">
                <tbody className="text-[#5C5650]">
                  <tr className="border-b border-[#F0EDE8] bg-[#FAFAF8]"><td className="py-2 px-3 font-medium w-36">프레임워크</td><td className="py-2 px-3">React 19 + TypeScript + Vite 8</td></tr>
                  <tr className="border-b border-[#F0EDE8]"><td className="py-2 px-3 font-medium">스타일링</td><td className="py-2 px-3">Tailwind CSS v3</td></tr>
                  <tr className="border-b border-[#F0EDE8] bg-[#FAFAF8]"><td className="py-2 px-3 font-medium">라우팅</td><td className="py-2 px-3">react-router-dom v7</td></tr>
                  <tr className="border-b border-[#F0EDE8]"><td className="py-2 px-3 font-medium">애니메이션</td><td className="py-2 px-3">Motion (Framer Motion)</td></tr>
                  <tr className="border-b border-[#F0EDE8] bg-[#FAFAF8]"><td className="py-2 px-3 font-medium">백엔드</td><td className="py-2 px-3">Supabase (Auth + Postgres + RLS + Edge Functions), EchoTwin API worker</td></tr>
                  <tr className="border-b border-[#F0EDE8]"><td className="py-2 px-3 font-medium">배포</td><td className="py-2 px-3">Cloudflare Pages (main 브랜치 push 자동 배포), R2(이미지 CDN)</td></tr>
                  <tr><td className="py-2 px-3 font-medium">AI</td><td className="py-2 px-3">Gemini (챗봇, Pages Function 프록시)</td></tr>
                </tbody>
              </table>
            </div>
          </section>

          {/* ===== 6. 개발 현황 ===== */}
          <section className="mb-10">
            <h2 className="text-lg font-bold text-[#1C1917] border-b border-[#E8E5E0] pb-2 mb-4">6. 개발 현황 요약</h2>
            <p className="text-sm text-[#5C5650] mb-3 leading-relaxed">
              1~12단계(메인, 물건, 교육, 상담/리포트, 인증, 마이페이지/커뮤니티 UI, 관리자, 자동 블로그, AI 시뮬레이터,
              낙찰인사이트, 입찰가 계산기)는 모두 완료. 이후 <strong className="text-[#1C1917]">실데이터 전환</strong> 단계가 진행 중이다.
            </p>
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3">
                <p className="text-xs font-bold text-emerald-700 mb-1">실연동 완료</p>
                <p className="text-[11px] text-[#5C5650] leading-relaxed">전문가 PICK · 교육과정 · 커뮤니티 · 블로그 · 자료실 · 인증 · 폼 제출 · 관리자 폼 접수함 · 챗봇</p>
              </div>
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                <p className="text-xs font-bold text-amber-700 mb-1">목업 잔존</p>
                <p className="text-[11px] text-[#5C5650] leading-relaxed">동영상 강의 · 리포트 구매 · 마이페이지 · 낙찰물건 인사이트 · 물건 비교 · 관리자 회원/결제/통계</p>
              </div>
            </div>
          </section>

          {/* ===== 7. 로드맵 / 백로그 ===== */}
          <section className="mb-10">
            <h2 className="text-lg font-bold text-[#1C1917] border-b border-[#E8E5E0] pb-2 mb-4">7. 다음 단계 — 개발 백로그</h2>

            <h3 className="text-sm font-bold text-[#C4956A] mb-2">7.1 데이터 실연동</h3>
            <div className="space-y-1.5 text-xs text-[#5C5650] mb-5">
              <div className="flex items-start gap-2"><i className="ri-checkbox-blank-circle-line text-[#C4956A] mt-0.5 text-[10px]"></i><span>동영상 강의·리포트·마이페이지·낙찰물건 인사이트·물건 비교 → Supabase 테이블화 (경계 규칙대로: 읽기 anon, 쓰기는 Edge Function 요청)</span></div>
              <div className="flex items-start gap-2"><i className="ri-checkbox-blank-circle-line text-[#C4956A] mt-0.5 text-[10px]"></i><span>관리자 대시보드 — 회원/결제/통계 탭 실연동 (현재 폼 접수함만 실연동)</span></div>
              <div className="flex items-start gap-2"><i className="ri-checkbox-blank-circle-line text-[#C4956A] mt-0.5 text-[10px]"></i><span>챗봇 SYSTEM_INSTRUCTION 하드코딩 추천 물건 3건 → useAuctionItems 실데이터로 치환</span></div>
              <div className="flex items-start gap-2"><i className="ri-checkbox-blank-circle-line text-[#C4956A] mt-0.5 text-[10px]"></i><span>결제 연동 (Stripe/Shopify) — 아직 미착수, 강의/리포트 구매가 실거래로 이어지지 않음</span></div>
              <div className="flex items-start gap-2"><i className="ri-checkbox-blank-circle-line text-[#C4956A] mt-0.5 text-[10px]"></i><span>정수강생 판정(수강권 체계) — 판정 정본 membership.ts isPaidMember() 자리만 만들어둔 상태(현재 전원 false)</span></div>
            </div>

            <h3 className="text-sm font-bold text-[#C4956A] mb-2">7.2 성능·정리</h3>
            <div className="space-y-1.5 text-xs text-[#5C5650] mb-5">
              <div className="flex items-start gap-2"><i className="ri-checkbox-blank-circle-line text-[#C4956A] mt-0.5 text-[10px]"></i><span>코드 스플리팅 — 번들 단일 2.0MB(gzip 566KB), xlsx·recharts는 admin 전용인데 전 방문자가 다운로드. 라우트 단위 React.lazy 권장</span></div>
              <div className="flex items-start gap-2"><i className="ri-checkbox-blank-circle-line text-[#C4956A] mt-0.5 text-[10px]"></i><span>index.html 정리 — canonical이 kangstudy.com으로 오기재(→ study.echotwin.kr), 미사용 font-awesome CDN 제거, OG 태그 실도메인화</span></div>
              <div className="flex items-start gap-2"><i className="ri-checkbox-blank-circle-line text-[#C4956A] mt-0.5 text-[10px]"></i><span>프로덕션 소스맵 비활성화(또는 hidden)</span></div>
              <div className="flex items-start gap-2"><i className="ri-checkbox-blank-circle-line text-[#C4956A] mt-0.5 text-[10px]"></i><span>Noto Sans KR 폰트 5종 로드 중 — 실사용 웨이트만 남기고 축소</span></div>
            </div>

            <h3 className="text-sm font-bold text-[#C4956A] mb-2">7.3 사업 판단 대기</h3>
            <div className="space-y-1.5 text-xs text-[#5C5650]">
              <div className="flex items-start gap-2"><i className="ri-question-line text-amber-500 mt-0.5"></i><span>강스터디에서 EchoTwin 매물 상세를 비회원에게 어디까지 공개할지 — worker 게이팅이 EchoTwin 과금모델을 그대로 적용하므로 현재 기본값은 안전측</span></div>
              <div className="flex items-start gap-2"><i className="ri-question-line text-amber-500 mt-0.5"></i><span>소개/후기/FAQ/문의/오시는길/약관 페이지 — 콘텐츠 확정 후 ComingSoon 해제</span></div>
            </div>
          </section>

          {/* ===== 8. 운영 구조 ===== */}
          <section className="mb-10">
            <h2 className="text-lg font-bold text-[#1C1917] border-b border-[#E8E5E0] pb-2 mb-4">8. 저장소·운영 구조</h2>
            <div className="space-y-2 text-xs text-[#5C5650]">
              <div className="flex items-start gap-2"><i className="ri-git-branch-line text-[#C4956A] mt-0.5"></i><span>이 저장소(<span className="font-mono">kangstudy-web</span>, 비공개)가 프론트 개발의 정본이다. 원래 EchoTwin 모노레포에서 subtree split로 분리되었으며, 외부 개발자가 이 저장소에서 작업한다.</span></div>
              <div className="flex items-start gap-2"><i className="ri-database-2-line text-[#C4956A] mt-0.5"></i><span>Supabase 프로젝트·auth.users는 EchoTwin과 공유(강스터디 로그인 = EchoTwin 로그인). DB 테이블은 kangstudy_ 접두사로 구분.</span></div>
              <div className="flex items-start gap-2"><i className="ri-cloud-line text-[#C4956A] mt-0.5"></i><span>배포: main 브랜치 push → Cloudflare Pages 자동 빌드(Git 연동), 커스텀 도메인 study.echotwin.kr</span></div>
              <div className="flex items-start gap-2"><i className="ri-user-settings-line text-[#C4956A] mt-0.5"></i><span>DB 스키마·RLS·Edge Function·환경변수는 오너(EchoTwin 측) 소관 — 신규 필드/엔드포인트는 오너 경유 요청</span></div>
            </div>
          </section>

          <div className="border-t border-[#E8E5E0] pt-6 text-xs text-[#8C8780]">
            <p>문서 버전 1.0 | 2026-08-18 | 강스터디 (study.echotwin.kr)</p>
            <p className="mt-1">본 문서는 강스터디 웹 프론트엔드의 제품 요구사항 정의서이며, 개발 이력 상세는 WORKLOG.md, 실행/경계 규칙은 README.md를 참고한다.</p>
          </div>

        </div>
      </div>
    </>
  );
}
